import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import classNames from 'classnames';
import { useAlert } from 'fasoo-ui-component-next';
import _ from 'lodash';
import qs from 'query-string';

import authApi from 'src/api/auth/authApi';
import useAuthStore from 'src/hooks/auth/useAuthStore';
import { getFasooUiComponentLanguage } from 'src/i18n';
import config from 'src/shared/config';
import { ResourceType, t } from 'src/shared/types/ResourceType';
import currentUrlUtils from 'src/shared/utils/currentUrlUtils';
import { appendLogoutCallbackEntity, getDeviceId } from 'src/shared/utils/sessionStorageManager';
import { AuthEntity, SignInReqEntity } from 'src/store/auth/auth';

import { CommonProps } from '../../components/helpers/props';
import AppBar from '../Layout/AppBar';

import LoginForm, { LoginErrorState } from './LoginForm';

import './index.scss';

const resource: ResourceType = {
  ko: {
    $: '로그인',
    id: '아이디',
    password: '비밀번호',
    'message.emptyId': '사용자 ID를 입력하십시오.',
    'message.emptyPassword': '비밀번호를 입력하십시오.',
    'login-failed': '로그인 실패',
    NO_APPROVER: '지정된 승인권자가 아니므로 로그인할 수 없습니다.', // 서버에서 처리하는 부분(일단 나온 리소스여서 유지)
  },
  en: {
    $: 'Login',
    id: 'ID',
    password: 'Password',
    'message.emptyId': 'Enter an user ID.',
    'message.emptyPassword': 'Enter a password.',
    'login-failed': 'Login Failed',
    NO_APPROVER: 'Cannot log in because you are not the designated approver.', // 서버에서 처리하는 부분(일단 나온 리소스여서 유지)
  },
};

export interface LoginState {
  userId: string;
  password: string;
}

export interface LoginProps extends CommonProps {
  lang?: 'ko' | 'en';
  contextPath?: string;
  isAuthenticated: boolean;
  authEntity: AuthEntity | undefined;
  signIn: (reqEntity: SignInReqEntity) => Promise<AuthEntity>;
  redirectToProduct: (userId: string, deviceId: string, now: number, redirectUrl: string) => void;
  redirectToProductUrls: string[];
}

const Login: React.FC<LoginProps> = ({
  lang = 'ko',
  contextPath = '/',
  isAuthenticated,
  authEntity,
  signIn,
  redirectToProduct,
  redirectToProductUrls,
}) => {
  const navigate = useNavigate();
  const { alert } = useAlert();

  // const [state, setState] = useState<LoginState>({
  //   userId: '',
  //   password: '',
  // });
  const [loginResult, setLoginResult] = useState(true);
  const [loginError, setLoginError] = useState<LoginErrorState | undefined>(undefined);
  const userIdInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const submitButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    userIdInputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (isAuthenticated && authEntity) {
      const redirectUrl = currentUrlUtils.getFirstQuery('redirectUrl') || `${contextPath}`; // config.url('/');
      // redirect 대상이 내부 페이지면 react-router 사용, 아니면 기본 redirect 사용.
      // fast일 경우에는 임시 token을 받아서 전달
      if (redirectUrl.startsWith(contextPath)) {
        navigate(redirectUrl);
      } else if (redirectUrl.startsWith('http')) {
        const findUrl = redirectToProductUrls.find((url) => redirectUrl.includes(url));
        if (findUrl) {
          // TODO FAST인지를 판단하는 방법 좀 더 nice하게는...
          // FAST는 로그아웃 url 등록하고, 임시 토큰 받아서 redirect
          const now = Date.now();
          redirectToProduct(authEntity.tokenEntity.userInfo.userId, getDeviceId(), now, findUrl);
        }
      } else {
        currentUrlUtils.setHref(redirectUrl);
      }
    }
  }, [alert, authEntity, navigate, isAuthenticated, redirectToProduct, redirectToProductUrls]);

  if (isAuthenticated && authEntity) {
    return null;
  }

  // const handleChangeInputEvt = (e: React.ChangeEvent<HTMLInputElement>): void => {
  //   setState({ ...state, [e.target.name]: e.target.value });
  // };

  // eslint-disable-next-line consistent-return
  const handleSignIn = (userId: string, password: string): void => {
    userIdInputRef.current?.blur();
    passwordInputRef.current?.blur();
    submitButtonRef.current?.blur();

    if (!userId) {
      userIdInputRef.current?.focus();
      return alert(t('message.emptyId', lang, resource), 'warning');
    }
    if (!password) {
      passwordInputRef.current?.focus();
      return alert(t('message.emptyPassword', lang, resource), 'warning');
    }
    signIn({
      userId,
      password,
      redirectUrl: currentUrlUtils.getFirstQuery('redirectUrl') || `${contextPath}`, // config.url('/'),
    })
      .then(() => {
        setLoginResult(true);
        // const { redirectUrl } = qs.parse(window.location.search);
        // if (redirectUrl && redirectUrl.length > 0) {
        //   history.push(Array.isArray(redirectUrl) ? redirectUrl[0] : redirectUrl);
        // } else {
        //   history.push(config.url('/'));
        // }
      })
      .catch((e) => {
        console.log('signIn', e);
        setLoginResult(false);
        setLoginError({
          name: e.name,
          message: e.message,
        });
        // if (e.message === 'NO_APPROVER') {
        //   console.log('signIn', e);
        //   setLoginError({
        //     name: 'NO_APPROVER',
        //     message:
        //       '사용자 ID 또는 비밀번호를 잘못 입력했습니다.<br/>입력하신 내용을 다시 확인해주세요.',
        //   });
        // }
        alert(e, 'warning', t('login-failed', lang, resource), undefined, true);
      });
  };

  return (
    <div className={classNames('mfxm-login-root')}>
      <AppBar />
      <LoginForm
        checkSaveUserId={() => console.log('checkSaveUserId')}
        loginResult={loginResult}
        loginError={loginError}
        handleSignIn={handleSignIn}
        initPWResult="initYet"
        userIdInputRef={userIdInputRef}
        passwordInputRef={passwordInputRef}
        submitButtonRef={submitButtonRef}
      />
    </div>
  );
};

const LoginOutside: React.FC = () => {
  const navigate = useNavigate();
  const { authEntity, signIn } = useAuthStore();

  const redirectProduct = (
    userId: string,
    deviceId: string,
    now: number,
    redirectUrl: string
  ): void => {
    authApi
      .sessionInit({
        userId,
        deviceId,
        timestamp: now,
        url: redirectUrl,
      })
      .then(({ timestamp, url }) => {
        if (now === timestamp) {
          // 추후 로그아웃 시 호출할 callback url을 저장
          const logoutUrl = currentUrlUtils.getFirstQuery('logoutUrl');
          const { bearer: bearerValues } = qs.parseUrl(url).query;
          const bearer = Array.isArray(bearerValues) ? _.head(bearerValues) : bearerValues;
          if (logoutUrl && bearer) {
            appendLogoutCallbackEntity({
              logoutUrl,
              token: bearer,
            });
          }
          currentUrlUtils.setHref(url);

          // console.log('LoginOutside');
          // console.log(url);
        } else {
          // TODO error handling
          console.log(`Invalid response of session init! (timestamp: ${timestamp}, now: ${now})`);
          navigate(config.url('/'));
        }
      })
      .catch((e) => {
        navigate(config.url('/'));
        alert(e);
      });
  };

  return (
    <Login
      lang={getFasooUiComponentLanguage()}
      contextPath={config.contextPath}
      authEntity={authEntity}
      isAuthenticated={authEntity !== undefined}
      signIn={signIn}
      redirectToProduct={(userId: string, deviceId: string, now: number, redirectUrl: string) => {
        redirectProduct(userId, deviceId, now, redirectUrl);
      }}
      redirectToProductUrls={['/fast']}
    />
  );
};

export default LoginOutside;
